// src/components/CircuitDatabase.js
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import 'flag-icons/css/flag-icons.min.css';
import '../styles.css';
import { countryCodes } from './CircuitEditor';

function getFlagEmoji(country) {
  const code = countryCodes[country];
  if (!code) return { code: '' };
  return { code: code.toLowerCase() };
}

function CircuitDatabase({ onClose, onSelect }) {
  const [user, loading, error] = useAuthState(auth);
  const [publicCircuits, setPublicCircuits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (loading) {
      return;
    }
    if (error || !user) {
      console.error('Auth error or no user:', error);
      return;
    }

    // Real-time listener for public circuits
    const circuitsQuery = query(
      collection(db, 'circuits'),
      where('isPublic', '==', true)
    );
    const unsubscribe = onSnapshot(circuitsQuery, (snapshot) => {
      const circuits = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setPublicCircuits(circuits);
    }, (err) => {
      console.error('Circuits fetch error:', err.message);
    });

    return () => {
      unsubscribe();
    };
  }, [user, loading, error]);

  // Memoize filtered circuits for performance
  const filteredCircuits = useMemo(() => {
    return publicCircuits
      .filter(circuit =>
        circuit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        circuit.country.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [publicCircuits, searchTerm]);

  return (
    <div className="overlay-card">
      <div className="card-container">
        <div className="page-title" style={{ marginBottom: '20px' }}>
          <h1>Circuit Database</h1>
          <button onClick={onClose} className="close-button">✕</button>
        </div>
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search Circuits By Name Or Country"
          className="event-editor-input"
        />
        <div className="session-data-card">
          {filteredCircuits.length === 0 && <p>No circuits found.</p>}
          <table className="circuits-table">
            <thead>
              <tr>
                <th></th>
                <th>Circuit Name</th>
                <th>Length</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCircuits.map(circuit => {
                const { code } = getFlagEmoji(circuit.country);
                return (
                  <tr key={circuit.id}>
                    <td className="flag-column">
                      <span className={`fi fi-${code}`} title={circuit.country}></span>
                    </td>
                    <td>{circuit.name}</td>
                    <td>{circuit.layouts ? circuit.layouts[0].length : circuit.length} km</td>
                    <td>
                      <button
                        onClick={() => onSelect(circuit)}
                        className="btn btn-group-action add"
                      >
                        +
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default CircuitDatabase;